<template lang="pug">
.roles
  AppAlert(
    v-if="processingInBackground"
    type="info"
    message="The member is being updated in the background..."
    icon-classes="hi hi-info"
  )
  .row
    .col-sm-6
      ValidationProvider(
        v-slot="{ errors }"
        name="role"
        rules="notUnassigned"
        :model-value="membershipRole"
      )
        #role.form-group(:class="{ 'has-error': errors[0] }")
          div
            .label-secondary.d-flex
              label.control-label.flex-1 Role *
              small(v-if="!fieldsDisabled")
                a(
                  href="https://support.hubstaff.com/hubstaff-user-rolepermissions-guide/"
                  target="_blank"
                  rel="noopener noreferrer"
                )
                  | Learn more
            AppSelect(
              v-model="membershipRole"
              :options="membershipRoleOptionsWithHints"
              :select2-options="roleSelect2Options"
              :disabled="roleDisabled || fieldsDisabled"
            )
            AppDialogError(:errors="errors")

            AppAlert.mt-10(
              v-if="showSilentAppPolicyWarning"
              type="warning"
              icon-classes="hi hi-warning"
              :message="silentAppPolicyWarningMessage"
            )

            .role-permissions(v-if="Object.keys(currentRolePermissions).length")
              .label-secondary.d-flex.align-items-center.mb-20
                label.control-label.mb-0.flex-1
                  | Permissions
                  i.hi.hi-info-filled.hi-12.a-tooltip.upgrade-warning.mx-5(
                    v-if="!atLeastPremiumPlan"
                    v-tooltip
                    title="Upgrade to a Team or Enterprise plan to access permissions to manage financials."
                  )
                a(
                  :href="permissionSettingsPath"
                  target="_blank"
                  :disabled="isDisabledRolePermissions"
                  :class="{ 'pointer-events-none': isDisabledRolePermissions }"
                )
                  i.hi.hi-16.hi-settings
                  span.ml-5.mr-10 Role permissions
              AppOnOffToggle.d-flex.is-regular.mb-10(
                v-for="(label, key) in currentRolePermissions"
                :id="key"
                :key="key"
                :value="label.value"
                :disabled="isDisabledRolePermissions"
                @input="label.toggleFunction"
              )
                | {{ label.label }} ({{ label.status }})
                i.hi.hi-info-filled.hi-12.a-tooltip.mx-5(
                  v-tooltip="{ html: true }"
                  style="vertical-align: baseline"
                  :title="label.descr"
                )
              AppConfirm.change-role-permission-confirmation-dialog(
                v-if="showToggleRolePermissionConfirmation"
                :title="rolePermissionConfirmationTitle"
                confirm-label="Confirm"
                size="medium"
                @cancel="onCancelTogglePermission"
                @confirm="toggleRolePermission(editedRolePermission)"
              )
                .dialog-warning.blue.small.d-flex
                  .icon-wrapper.d-flex.justify-content-center
                    i.hi.hi-info-filled.hi-12
                  p.mb-0 You're about to make this permission an exception. This can be modified at any time from the member profile or at the&nbsp;
                    |
                    a.clickable.bold(
                      :href="rolePermissionsLink"
                      target="_blank"
                    ) roles permissions page
                p(v-html="toggleRolePermissionConfirmationText")
                p.question Do you want to proceed with changing this permission?

  template(v-if="hasProjects")
    .row
      .col-md-6
        div(v-if="showProjectsSelect")
          ValidationProvider(
            v-slot="{ errors }"
            name="projects"
            :rules="projectsRequiredRules"
            :model-value="userProjects"
          )
            #projects.form-group(:class="{ 'has-error': errors[0] }")
              .d-flex.align-items-center
                h6.heading Projects
                i.hi.hi-info-filled.hi-12.a-tooltip.mx-5(
                  v-tooltip
                  :title="editProjectsMembershipTooltip"
                )
              .label-secondary.d-flex(v-if="!disabled")
                span.black-lighter.mr-5 Able to track time on these projects
                a.select-all(@click="selectAllProjects('user-selector')") Select all
              AppLazySelect(
                :key="userProjectsKey"
                ref="user-selector"
                v-model="userProjects"
                eager
                multiple
                append-to-body
                resource="project"
                :fetcher="fetcher"
                :organization-id="organizationId"
                :disabled="fieldsDisabled"
              )
              .info.black-lighter Organization owner and organization managers can view and edit all projects by default
              AppDialogError(:errors="errors")
        div(v-if="!showProjectsSelect")
          div(v-if="membershipRole !== 'project_viewer'")
            .d-flex.align-items-center
              h6.heading Projects
              i.hi.hi-info-filled.hi-12.a-tooltip.mx-5(
                v-tooltip
                :title="editProjectsMembershipTooltip"
              )
            .d-flex.flex-column
              #user.form-group
                .label-secondary.d-flex(v-if="!fieldsDisabled")
                  span.black-lighter.mr-5 Able to track time on these projects
                  a.select-all(@click="selectAllProjects('user-selector')") Select all
                AppLazySelect(
                  :key="userProjectsKey"
                  ref="user-selector"
                  v-model="userProjects"
                  eager
                  multiple
                  append-to-body
                  resource="project"
                  :fetcher="fetcher"
                  :organization-id="organizationId"
                  :disabled="fieldsDisabled"
                )
              #manager.form-group
                .label-secondary.d-flex(v-if="!fieldsDisabled")
                  span.black-lighter.mr-5 Manages these projects as
                  span.bold Project manager
                  a.select-all(@click="selectAllProjects('manager-selector')") Select all
                AppLazySelect(
                  :key="managerProjectsKey"
                  ref="manager-selector"
                  v-model="managerProjects"
                  eager
                  multiple
                  append-to-body
                  resource="project"
                  :fetcher="fetcher"
                  :organization-id="organizationId"
                  :disabled="fieldsDisabled"
                )
                span.black-lighter.small.pm-warning(v-if="ownerOrManager")
                  | Organization Owners and Organization Managers can manage all projects by default
      .col-md-6
        div(v-if="showTeamRoleSelect")
          .d-flex.align-items-center
            h6.heading Teams
            i.hi.hi-info-filled.hi-12.a-tooltip.mx-5(
              v-tooltip
              :title="editTeamsMembershipTooltip"
            )
          template(v-if="showTeamRoleSelect")
            #member-teams.form-group
              .label-secondary.d-flex(v-if="!fieldsDisabled")
                span.black-lighter.mr-5 Member in these teams
                a.select-all(@click="selectAll('user', 'team')") Select all
              AppSelect(
                ref="user-teams-select"
                v-model="userTeams"
                :disabled="fieldsDisabled"
                :options="userTeamsOptions"
                :select2-options="teamSelect2Options"
                :prevent-clear-ids="selectedScimManagedTeamIds"
              )
            #leaded-teams.form-group
              .label-secondary.d-flex(v-if="!fieldsDisabled")
                span.black-lighter.mr-5 Manages these teams as
                span.bold Team lead
                a.select-all(@click="selectAll('lead', 'team')") Select all
              AppSelect(
                ref="lead-teams-select"
                v-model="leadedTeams"
                :prevent-clear-ids="selectedScimManagedTeamIds"
                :disabled="fieldsDisabled"
                :options="leadTeamsOptions"
                :select2-options="teamSelect2Options"
              )

  .row
    .col-md-6
      div(v-if="!showProjectsSelect && showViewerProjects")
        .d-flex.align-items-center
          h6.heading Viewer
          i.hi.hi-info-filled.hi-12.a-tooltip.mx-5(
            v-tooltip
            :title="editProjectViewerTooltip"
          )
        ValidationProvider(
          ref="viewerProvider"
          v-slot="{ errors }"
          name="viewerProjects"
          :rules="projectOrTeamRequiredRules"
          :model-value="viewerProjects"
        )
          #viewer.form-group(:class="{ 'has-error': errors[0] }")
            .label-secondary.d-flex(v-if="!fieldsDisabled")
              span.black-lighter.mr-5 Views these projects
              a.select-all(@click="selectAllProjects('viewer-selector')") Select all
            AppLazySelect(
              :key="viewerProjectsKey"
              ref="viewer-selector"
              v-model="viewerProjects"
              eager
              multiple
              append-to-body
              resource="project"
              :fetcher="fetcher"
              :organization-id="organizationId"
              :disabled="fieldsDisabled"
            )
            AppDialogError(:errors="errors")
    .col-md-6
      div(v-if="showTeamViewer")
        .d-flex.align-items-center(v-if="!showTeamRoleSelect")
          h6.heading Teams
          i.hi.hi-info-filled.hi-12.a-tooltip.mx-5(
            v-tooltip
            :title="editTeamsMembershipTooltip"
          )
        ValidationProvider(
          ref="viewerTeamsProvider"
          v-slot="{ errors }"
          name="viewerTeams"
          :rules="projectOrTeamRequiredRules"
          :model-value="viewerTeams"
        )
          #viewer-teams.form-group(:class="{ 'has-error': errors[0], 'align-padding': showTeamRoleSelect }")
            .label-secondary.d-flex(v-if="!fieldsDisabled")
              span.black-lighter.mr-5 Project viewer
              span.bold via teams
              a.select-all(@click="selectAll('viewer', 'team')") Select all
            AppSelect(
              ref="viewer-teams-select"
              v-model="viewerTeams"
              :prevent-clear-ids="selectedScimManagedTeamIds"
              :disabled="fieldsDisabled"
              :options="viewerTeamsOptions"
              :select2-options="teamSelect2Options"
            )
            AppDialogError(:errors="errors")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { extend } from '~/utils/vee_validate_compat';
import { OrganizationsResource, UserOrganizationsResource } from '~/resources';

extend('notUnassigned', {
  validate: (value) => {
    return value !== 'unassigned';
  },
  message: 'A valid role must be chosen',
});

extend('projectsRequired', {
  params: [{ name: 'role', isTarget: true }, { name: 'countingModel' }],
  validate: (value, { role, countingModel }) => {
    // if we have any projects we are fine
    if (value?.length > 0) {
      return true;
    }

    // If we are choosing a project manager they must specify at least one project
    if (role === 'project_manager') {
      return false;
    }

    // depending on the counting model either viewer or user requires at least one project
    return role !== (countingModel === 'all_non_viewers' ? 'project_viewer' : 'project_user');
  },
  computesRequired: true,
  message: 'at least one project must be selected for this role',
});

extend('projectOrTeamRequired', {
  params: ['projectsCount', 'showTeamViewer'],
  computesRequired: true,
  validate: (value, { projectsCount }) => {
    return value?.length > 0 || projectsCount > 0;
  },
  message: (_fieldName, placeholders) => {
    return `at least one project ${placeholders.showTeamViewer ? 'or team' : ''} must be selected for this role`;
  },
});

const BASE_SELECT2_OPTS = Object.freeze({
  allowClear: true,
  multiple: true,
  width: '100%',
});

export default {
  name: 'MemberDialogTabProjectsRolesNew',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    processingInBackground: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    projectSelect2Options: Object.freeze({ ...BASE_SELECT2_OPTS, placeholder: 'Select projects' }),
    teamSelect2Options: Object.freeze({ ...BASE_SELECT2_OPTS, placeholder: 'Select teams' }),
    roleSelect2Options: Object.freeze({ width: '100%' }),
    showToggleRolePermissionConfirmation: false,
    editedRolePermission: '',
    userProjectsKey: 0,
    managerProjectsKey: 0,
    viewerProjectsKey: 0,
    tooltipRefreshTimeout: null,
  }),
  computed: {
    ...mapState({
      membership: 'membership',
      initialMultipleProjects: 'initial_multiple_projects',
      editProjectsMembershipTooltip: 'edit_projects_membership_tooltip',
      selectedProjects: 'selected_projects',
      canUseTeamRoleSelectors: 'can_use_team_role_selectors',
    }),
    ...mapGetters([
      'allProjectOptions',
      'membershipRoleOptions',
      'multipleProjectRoles',
      'roleDisabled',
      'projectOptions',
      'teamsOptions',
      'hasFeature',
      'countingModel',
      'allowManagerRoles',
      'restrictedTooltip',
      'canEditRolePermissions',
      'atLeastPremiumPlan',
      'defaultRolePermissionsList',
      'rolePermissions',
      'expectedWorkEnabled',
      'permissionSettingsPath',
      'organizationId',
    ]),
    membershipRoleOptionsWithHints() {
      return this.membershipRoleOptions.map((m) => ({
        ...m,
        disabledHint: !this.allowManagerRoles ? this.restrictedTooltip : null,
      }));
    },
    projectsRequiredRules() {
      return {
        projectsRequired: {
          role: 'role',
          countingModel: this.countingModel,
        },
      };
    },
    projectOrTeamRequiredRules() {
      if (this.membershipRole === 'project_viewer') {
        return {
          projectOrTeamRequired: {
            projectsCount: this.viewerProjects.length + this.viewerTeams.length,
            showTeamViewer: this.showTeamViewer,
          },
        };
      }

      return {};
    },
    currentRolePermissions() {
      const rawPermissions = this.rolePermissions[this.membershipRole];

      if (!rawPermissions) {return {};}

      return Object.fromEntries(
        Object.entries(rawPermissions).map(([k, v]) => [
          k,
          {
            ...v,
            value: this.includesRolePermission(k),
            toggleFunction: (_value, event) => this.onToggleRolePermission(k, event),
            status: this.rolePermissionStatus(k),
          },
        ]),
      );
    },
    hasProjects() {
      return this.hasFeature('projects');
    },
    showSilentAppPolicyWarning() {
      if (!this.membership.in_corporate_auto_track_policy) {
        return false;
      }

      if (this.wasEffectiveViewerAtLoad) {
        return false;
      }

      return this.membershipRole === 'project_viewer' || this.becomesEffectiveViewer;
    },
    wasEffectiveViewerAtLoad() {
      const { user = [], manager = [], lead = [], viewer = [] } = this.initialMultipleProjects || {};

      return !user.length && !manager.length && !lead.length && viewer.length > 0;
    },
    becomesEffectiveViewer() {
      if (['organization_owner', 'organization_manager', 'project_viewer'].includes(this.membershipRole)) {
        return false;
      }

      const { user, manager, lead, viewer } = this.membership.projects;

      return !user.length && !manager.length && !lead.length && viewer.length > 0;
    },
    silentAppPolicyWarningMessage() {
      if (this.membershipRole === 'project_viewer') {
        return 'This member is tracked by a Silent app policy. Saving the Project viewer role will remove them from the policy and stop silent time tracking.';
      }

      return 'This member is tracked by a Silent app policy. These changes leave them with viewer-only access, so saving will remove them from the policy and stop silent time tracking.';
    },
    membershipRole: {
      get() {
        return this.membership.role;
      },
      set(role) {
        this.updateRole(role);
        this.viewerProjectsKey++;
      },
    },
    managerProjects: {
      get() {
        return this.membership.projects.manager.filter((id) => id.startsWith('p_'));
      },
      set(projects) {
        this.updateMultipleProjects({ field: 'manager', val: projects });
        this.userProjectsKey++;
        this.viewerProjectsKey++;
      },
    },
    userProjects: {
      get() {
        return this.membership.projects.user.filter((id) => id.startsWith('p_'));
      },
      set(projects) {
        this.updateMultipleProjects({ field: 'user', val: projects });
        this.viewerProjectsKey++;
        this.managerProjectsKey++;
      },
    },
    viewerProjects: {
      get() {
        return this.membership.projects.viewer.filter((id) => id.startsWith('p_'));
      },
      set(projects) {
        this.updateMultipleProjects({ field: 'viewer', val: projects });
        this.userProjectsKey++;
        this.managerProjectsKey++;
      },
    },
    leadedTeams: {
      get() {
        return this.membership.projects.lead;
      },
      set(teams) {
        const currentTeams = this.membership.projects.lead.filter((id) => id.startsWith('t_'));
        const scimTeamsToKeep = currentTeams.filter((id) => {
          const option = this.teamsOptions.find((opt) => opt.id === id);

          return option && option.scimManaged;
        });
        const finalTeams = [...new Set([...teams, ...scimTeamsToKeep])];

        this.updateLeadedTeams(finalTeams);
      },
    },
    viewerTeams: {
      get() {
        return this.membership.projects.viewer.filter((id) => id.startsWith('t_'));
      },
      set(teams) {
        const currentTeams = this.membership.projects.viewer.filter((id) => id.startsWith('t_'));
        const scimTeamsToKeep = currentTeams.filter((id) => {
          const option = this.teamsOptions.find((opt) => opt.id === id);

          return option && option.scimManaged;
        });
        const finalTeams = [...new Set([...teams, ...scimTeamsToKeep])];

        this.updateMultipleTeams({ field: 'viewer', val: finalTeams });
      },
    },
    userTeams: {
      get() {
        return this.membership.projects.user
          .concat(this.membership.projects.manager)
          .filter((id) => id.startsWith('t_'));
      },
      set(teams) {
        const currentTeams = this.membership.projects.user
          .concat(this.membership.projects.manager)
          .filter((id) => id.startsWith('t_'));
        const scimTeamsToKeep = currentTeams.filter((id) => {
          const option = this.teamsOptions.find((opt) => opt.id === id);

          return option && option.scimManaged;
        });
        const finalTeams = [...new Set([...teams, ...scimTeamsToKeep])];

        this.updateMultipleTeams({ field: 'user', val: finalTeams });
      },
    },
    isDisabledRolePermissions() {
      return !this.atLeastPremiumPlan || !this.canEditRolePermissions;
    },
    fieldsDisabled() {
      return this.disabled || this.processingInBackground;
    },
    showProjectsSelect() {
      return ['organization_owner'].includes(this.membershipRole);
    },
    showViewerProjects() {
      return !['organization_owner', 'organization_manager'].includes(this.membershipRole);
    },
    showTeamRoleSelect() {
      return this.membershipRole !== 'project_viewer' && this.canUseTeamRoleSelectors;
    },
    ownerOrManager() {
      return ['organization_owner', 'organization_manager'].includes(this.membershipRole);
    },
    selectedScimManagedTeamIds() {
      const membershipProjects = this.membership.projects;
      const all = [
        ...(membershipProjects.user || []),
        ...(membershipProjects.manager || []),
        ...(membershipProjects.lead || []),
        ...(membershipProjects.viewer || []),
      ].filter((id) => id.startsWith('t_'));
      const scimManagedIds = all.filter((id) => {
        const opt = this.teamsOptions.find((o) => o.id === id);

        return opt && opt.scimManaged;
      });

      return Array.from(new Set(scimManagedIds));
    },
    userTeamsOptions() {
      return this.getContextAwareTeamOptions('user');
    },
    leadTeamsOptions() {
      return this.getContextAwareTeamOptions('lead');
    },
    viewerTeamsOptions() {
      return this.getContextAwareTeamOptions('viewer');
    },
    showTeamViewer() {
      return ['project_viewer', 'by_project'].includes(this.membershipRole) && this.canUseTeamRoleSelectors;
    },
    editTeamsMembershipTooltip() {
      return 'Teams makes assigning multiple members to a project easier. Assign a Team Lead to manage the team.';
    },
    editProjectViewerTooltip() {
      return "Project viewers can view reports for any projects they're added to.";
    },
    scimClearTooltipMessage() {
      return 'Some teams are managed by SCIM and cannot be removed';
    },
    teamSelectorConfigs() {
      return [
        { ref: 'user-teams-select', teams: this.userTeams },
        { ref: 'lead-teams-select', teams: this.leadedTeams },
        { ref: 'viewer-teams-select', teams: this.viewerTeams },
      ];
    },
    rolePermissionLabel() {
      return this.currentRolePermissions[this.editedRolePermission].label;
    },
    rolePermissionConfirmationTitle() {
      const rolePermissionValue = this.includesRolePermission(this.editedRolePermission) ? 'OFF' : 'ON';

      return `Confirm changes - Turn ${rolePermissionValue} ${this.rolePermissionLabel}?`;
    },
    rolePermissionsLink() {
      return OrganizationsResource.rolePermissionsLink({ id: this.$store.state.organization.id });
    },
    toggleRolePermissionConfirmationText() {
      const verb = this.includesRolePermission(this.editedRolePermission) ? 'not have' : 'have';

      return `${this.membership.user.name} will ${verb} access to "${this.rolePermissionLabel}". By changing this permission it will override the <b>default organization permission</b> making this setting an <b>exception</b> at the member level.`;
    },
  },
  watch: {
    membershipRole(newRole, oldRole) {
      if (newRole === 'project_viewer') {
        this.viewerTeams = this.viewerTeams.concat(this.leadedTeams.concat(this.userTeams));
        this.viewerProjects = this.viewerProjects.concat(this.managerProjects.concat(this.userProjects));
        if (!this.viewerProjects.length && !this.viewerTeams.length) {
          this.$nextTick(() => {
            this.$refs.viewerProvider.validate();
            this.$refs.viewerTeamsProvider?.validate();
          });
        }
      }
      if (['organization_owner'].includes(newRole)) {
        this.userTeams = this.userTeams.concat(this.viewerTeams);
        this.userProjects = this.userProjects.concat(this.managerProjects.concat(this.viewerProjects));
      }

      // Project viewer is read-only at the UI level and shouldn't carry role permissions.
      if (newRole === 'project_viewer') {
        this.updateRolePermissions([]);

        return;
      }

      // FE role symbols collapse to one of these BE-level role categories.
      // Permissions are tied to the BE role, so we only reset them when the BE role actually changes.
      const FE_ROLE_TO_BE_CATEGORY = {
        organization_owner: 'organization_owner',
        organization_manager: 'organization_manager',
        project_viewer: 'organization_user',
        project_user: 'organization_user',
        project_manager: 'organization_user',
        by_project: 'organization_user',
      };
      const newCategory = FE_ROLE_TO_BE_CATEGORY[newRole];
      const oldCategory = FE_ROLE_TO_BE_CATEGORY[oldRole];

      if (newCategory && newCategory === oldCategory) {return;}

      const defaultRolePermissions = this.defaultRolePermissionsList[newCategory] || [];

      this.updateRolePermissions(defaultRolePermissions);
    },
    viewerProjects(_newVal) {
      if (this.membershipRole === 'project_viewer') {
        this.$nextTick(() => this.$refs.viewerProvider.validate());
      }
    },
    viewerTeams(_newVal) {
      if (this.membershipRole === 'project_viewer') {
        this.$nextTick(() => this.$refs.viewerProvider.validate());
      }
      this.refreshTooltips();
    },

    userTeams() {
      this.refreshTooltips();
    },
    leadedTeams() {
      this.refreshTooltips();
    },
  },
  mounted() {
    setTimeout(() => {
      this.setupClearButtonTooltips();
    }, 500);
  },
  beforeUnmount() {
    clearTimeout(this.tooltipRefreshTimeout);
    this.tooltipRefreshTimeout = null;
    this.removeClearButtonTooltips();
  },
  methods: {
    ...mapActions([
      'updateRole',
      'updateProjects',
      'updateMultipleProjects',
      'updateMultipleTeams',
      'updateLeadedTeams',
      'updateRolePermissions',
    ]),
    selectAll(field, model = 'project') {
      let val;

      if (model === 'team') {
        const optionsGetter =
          field === 'lead' ? 'leadTeamsOptions' : field === 'viewer' ? 'viewerTeamsOptions' : 'userTeamsOptions';

        val = this[optionsGetter]
          .filter((t) => {
            return !t.disabled;
          })
          .map((t) => t.id);
      } else {
        val = this.allProjectOptions;
      }

      if (field === 'lead') {
        this.leadedTeams = val;
      } else if (field && model === 'team') {
        if (field === 'viewer') {
          this.viewerTeams = val;
        } else {
          this.userTeams = val;
        }
      } else if (field) {
        this.updateMultipleProjects({ field, val });
      } else {
        this.updateProjects(val);
      }
    },
    includesRolePermission(permissionKey) {
      return (this.membership.role_permissions || []).includes(permissionKey);
    },
    isDefaultState(permissionKey) {
      return (
        (this.membership.role_permissions || []).includes(permissionKey) ===
        (this.membership.default_role_permissions || []).includes(permissionKey)
      );
    },
    rolePermissionStatus(permissionKey) {
      return this.isDefaultState(permissionKey) ? 'Default' : 'Exception';
    },
    onToggleRolePermission(permissionKey, event) {
      if (this.isDefaultState(permissionKey)) {
        event.preventDefault();
        this.editedRolePermission = permissionKey;
        this.showToggleRolePermissionConfirmation = true;
      } else {
        this.toggleRolePermission(permissionKey);
      }
    },
    onCancelTogglePermission() {
      this.showToggleRolePermissionConfirmation = false;
    },
    toggleRolePermission(permissionKey) {
      let permissions = this.membership.role_permissions || [];

      if (permissions.includes(permissionKey)) {
        permissions = permissions.filter((key) => key !== permissionKey);
      } else {
        permissions = permissions.concat([permissionKey]);
      }
      this.updateRolePermissions(permissions);

      this.showToggleRolePermissionConfirmation = false;
    },
    fetcher(payload) {
      return UserOrganizationsResource.projects({ id: this.organizationId }, payload, {
        abortReload: true,
      });
    },
    selectAllProjects(ref) {
      this.$refs[ref].onSelectAll();
    },
    getContextAwareTeamOptions(currentRole) {
      const membershipProjects = this.membership.projects;
      const roleTeamMap = {
        user: new Set(
          [...(membershipProjects.user || []), ...(membershipProjects.manager || [])].filter((id) =>
            id.startsWith('t_'),
          ),
        ),
        lead: new Set((membershipProjects.lead || []).filter((id) => id.startsWith('t_'))),
        viewer: new Set((membershipProjects.viewer || []).filter((id) => id.startsWith('t_'))),
      };

      return this.teamsOptions.map((team) => {
        let disabled = team.disabled;
        let disabledHint = team.disabledHint;

        if (team.scimManaged) {
          const isInCurrentRole = roleTeamMap[currentRole]?.has(team.id);

          if (isInCurrentRole) {
            disabled = true;
            disabledHint = 'Team managed by SCIM';
          }
        }

        return { ...team, disabled, disabledHint };
      });
    },
    setupClearButtonTooltips() {
      this.$nextTick(() => {
        this.teamSelectorConfigs.forEach((config) => {
          const selectComponent = this.$refs[config.ref];

          if (selectComponent) {
            const hasScimTeamsInThisSelector = (config.teams || []).some((teamId) => {
              const teamOption = this.teamsOptions.find((opt) => opt.id === teamId);

              return teamOption && teamOption.scimManaged;
            });

            if (hasScimTeamsInThisSelector) {
              this.addTooltipToClearButton(selectComponent.$el, this.scimClearTooltipMessage);
            }
          }
        });
      });
    },
    addTooltipToClearButton(selectElement, tooltipMessage) {
      const clearButton = $(selectElement).find('.select2-selection__clear');

      if (clearButton.length) {
        if (clearButton.data('bs.tooltip')) {
          clearButton.tooltip('dispose');
        }

        clearButton.attr('title', tooltipMessage);
        clearButton.tooltip({
          placement: 'top',
          container: 'body',
          trigger: 'hover',
        });

        clearButton.off('click.clearTooltip').on('click.clearTooltip', () => {
          this.handleClearButtonClick(clearButton);
        });
      }
    },
    handleClearButtonClick(clearButton) {
      if (clearButton.data('bs.tooltip')) {
        clearButton.tooltip('hide');
        clearButton.tooltip('dispose');
      }

      clearButton.off('click.clearTooltip');

      setTimeout(() => {
        this.setupClearButtonTooltips();
      }, 100);
    },
    removeClearButtonTooltips() {
      this.teamSelectorConfigs.forEach((config) => {
        const selectComponent = this.$refs[config.ref];

        if (selectComponent?.$el) {
          const clearButton = selectComponent.$el.querySelector('.select2-selection__clear');

          if (clearButton) {
            $(clearButton).off('click.clearTooltip');
            if ($(clearButton).data('bs.tooltip')) {
              $(clearButton).tooltip('dispose');
            }
            clearButton.removeAttribute('title');
          }
        }
      });

      if (typeof document !== 'undefined') {
        document.querySelectorAll('.tooltip').forEach((tooltip) => tooltip.remove());
      }
    },
    refreshTooltips() {
      clearTimeout(this.tooltipRefreshTimeout);
      this.tooltipRefreshTimeout = null;
      this.tooltipRefreshTimeout = setTimeout(() => {
        this.removeClearButtonTooltips();
        this.$nextTick(() => {
          this.setupClearButtonTooltips();
        });
      }, 50);
    },
  },
};
</script>
